/**
 * Wraps the app: shows sign-in, MFA, the lock screen or the app, and runs
 * the client-side idle lock. While a sign-in is in progress the flow stays
 * on screen until the person has picked an office, even though the session
 * is already active on the server.
 */
import { PRODUCT } from '@velora/contracts';
import { BrandMark, Button, Spinner } from '@velora/ui';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useSessionContext, useIdleLock } from '../session.js';
import { ImpersonationBanner } from './ImpersonationBanner.js';
import { LockScreen } from './LockScreen.js';
import { SignInFlow } from './SignIn.js';
import '../identity.css';

function Splash({ children }: { children?: ReactNode }) {
  return (
    <div className="idn-splash" role="status" aria-live="polite">
      <BrandMark name={PRODUCT.name} size={40} />
      {children ?? (
        <>
          <Spinner />
          <span className="idn-sub">Opening {PRODUCT.shortName}</span>
        </>
      )}
    </div>
  );
}

/**
 * The tab title while the gate covers the app. Without it the tab keeps the
 * last page's title ("Home - ...") on the sign-in and lock screens. The app's
 * pages set their own title again when they mount after the gate opens.
 */
export function gateTitle(screen: 'sign-in' | 'locked' | 'loading'): string {
  const label = screen === 'locked' ? 'Screen locked' : screen === 'sign-in' ? 'Sign in' : null;
  return label ? `${label} - ${PRODUCT.name}` : PRODUCT.name;
}

function GateTitle({ screen }: { screen: Parameters<typeof gateTitle>[0] }) {
  useEffect(() => {
    document.title = gateTitle(screen);
  }, [screen]);
  return null;
}

const InsideGate = createContext(false);

/** Gates its children behind sign-in. Nested gates pass through. */
export function AuthGate({ children }: { children: ReactNode }) {
  return useContext(InsideGate) ? <>{children}</> : <Gate>{children}</Gate>;
}

function Gate({ children }: { children: ReactNode }) {
  const { state, refresh, lockNow, session } = useSessionContext();
  const [flowOpen, setFlowOpen] = useState(false);
  const onIdle = useCallback(() => {
    void lockNow();
  }, [lockNow]);
  useIdleLock(
    state.kind === 'active' && !flowOpen ? (session?.session.idleLockSeconds ?? null) : null,
    onIdle,
  );

  if (state.kind === 'loading')
    return (
      <>
        <GateTitle screen="loading" />
        <Splash />
      </>
    );
  if (state.kind === 'unavailable') {
    return (
      <Splash>
        <GateTitle screen="loading" />
        <p className="idn-sub">{state.message}</p>
        <Button variant="secondary" onClick={() => void refresh()}>
          Try again
        </Button>
      </Splash>
    );
  }
  if (state.kind === 'locked') {
    return (
      <>
        <GateTitle screen="locked" />
        <LockScreen
          peek={state.peek}
          onUnlocked={() => void refresh()}
          onSignedOut={() => void refresh()}
        />
      </>
    );
  }
  if (state.kind !== 'active' || flowOpen) {
    return (
      <>
        <GateTitle screen="sign-in" />
        <SignInFlow
          initial={state}
          onStart={() => setFlowOpen(true)}
          onFinished={() => {
            setFlowOpen(false);
            void refresh();
          }}
        />
      </>
    );
  }
  return (
    <InsideGate.Provider value>
      <ImpersonationBanner />
      {children}
    </InsideGate.Provider>
  );
}
